import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCarouselBanners } from '../redux/slices/bannerSlice';
import LottiePlayer from './LottiePlayer';

const styles = {
    carouselDot: {
        height: '8px',
        width: '8px',
        backgroundColor: '#E5E7EB',
        borderRadius: '50%',
        display: 'inline-block',
        margin: '0 4px',
    },
    carouselDotActive: {
        backgroundColor: '#FFFFFF',
    },
    navButton: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        color: '#FFFFFF',
        padding: '10px',
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'background-color 0.3s',
    },
    navButtonHover: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
};

const Banner = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const dispatch = useDispatch();
    const { carouselBanners, loading, error } = useSelector((state) => state.banner);

    useEffect(() => {
        dispatch(fetchCarouselBanners());
    }, [dispatch]);

    // Auto-scroll effect
    useEffect(() => {
        if (carouselBanners.length === 0) return;
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % carouselBanners.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [carouselBanners.length]);

    // Handle manual navigation
    const goToPrevious = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? carouselBanners.length - 1 : prevIndex - 1
        );
    };

    const goToNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % carouselBanners.length);
    };
    if (loading) {
        return (
            <div className="flex items-center justify-center h-[20rem] bg-custom-blue rounded-3xl">
                <LottiePlayer
                    src="https://lottie.host/b2b25f7e-1811-405a-9986-2d09791950ef/8cJ75x5MYO.lottie"
                    width={300}
                    height={300}
                />

            </div>
        );
    }

    if (error || carouselBanners.length === 0) {
        return (
            <div className="h-[20rem] flex items-center justify-center text-custom-white bg-custom-blue rounded-3xl">
                {error || 'No banners available'}
            </div>
        );
    };

    return (
        <div className="relative w-full h-[20rem] rounded-3xl overflow-hidden bg-custom-blue">
            {/* Image Slides */}
            <div
                className="flex h-full transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
                {carouselBanners.map((banner, index) => (
                    <div key={banner._id} className="min-w-full h-full">
                        <img
                            src={banner.imageUrl}
                            alt={banner.altText}
                            className="w-full h-full object-cover"
                        />
                    </div>
                ))}
            </div>

            {/* Navigation Buttons */}
            <button
                style={{ ...styles.navButton, left: '10px' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.navButtonHover.backgroundColor)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = styles.navButton.backgroundColor)}
                onClick={goToPrevious}
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
            </button>
            <button
                style={{ ...styles.navButton, right: '10px' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.navButtonHover.backgroundColor)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = styles.navButton.backgroundColor)}
                onClick={goToNext}
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Dots for Navigation */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {carouselBanners.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`w-3 h-3 rounded-full ${currentIndex === index ? 'bg-custom-yellow' : 'bg-custom-white bg-opacity-50'
                            }`}
                        aria-label={`Go to image ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default Banner;